import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IColors } from '@app/core/interfaces/color.interface';

interface IBadgeVariantConfig {
  badgeClass: string;
  iconClass?: string;
  leadingIcon?: string;
  trailingIcon?: string;
}

type IBadgeVariant =
  | 'complete'
  | 'in-progress'
  | 'warning'
  | 'positive'
  | 'neutral'
  | 'informative'
  | 'negative';
@Component({
  selector: 'ag1-badge-base',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Ag1BadgeBaseComponent {
  /**
   * @param label What you'd like to display in the badge
   * @example 'Delivered'
   * */
  @Input() label: string;
  /**
   * @param color The color of the badge
   * @example 'green'
   * */
  @Input() color: IColors = 'slate';
  /**
   * @param colorClassOverride If you want to override the default color classes, you can pass in a string here
   * @example 'is-line-item-accepted'
   */
  @Input() colorClassOverride: string;

  /**
   * @param leadingIcon If you want to add an icon to the leading side of the badge
   * @example '/assets/icons/general/check.svg'
   */
  @Input() leadingIcon: string;

  /**
   * @param trailingIcon If you want to add an icon to the trailing side of the badge
   * @example '/assets/icons/general/check.svg'
   */
  @Input() trailingIcon: string;

  /**
   * @param variant The variant of the badge which is used to select a predefined badge style
   */
  @Input() variant: IBadgeVariant;

  private readonly _variantConfig: Record<IBadgeVariant, IBadgeVariantConfig> =
    {
      complete: {
        iconClass: 'text-green-600',
        badgeClass: 'bg-green-100 text-green-700 border border-green-400',
        leadingIcon: '/assets/icons/general/system-feedback-success.svg',
      },
      'in-progress': {
        iconClass: 'text-[#64366d]',
        badgeClass: 'text-[#64366d] border border-[#ab60bd] bg-[#efdff5]',
        leadingIcon: '/assets/icons/general/system-feedback-pending.svg',
      },
      warning: {
        iconClass: 'text-text-warning-default',
        badgeClass:
          'bg-interface-warning-muted-default text-text-warning-muted-default border border-interface-warning-border-default',
        leadingIcon: '/assets/icons/general/system-feedback-warning.svg',
      },
      positive: {
        iconClass: 'text-text-positive-default',
        badgeClass:
          'bg-interface-positive-muted-default text-text-positive-default border border-interface-positive-border-default',
        leadingIcon: '/assets/icons/general/system-feedback-dot.svg',
      },
      neutral: {
        iconClass: 'text-slate-800',
        badgeClass: 'bg-slate-200 text-slate-800 border border-slate-300',
        leadingIcon: '/assets/icons/general/info.svg',
      },
      informative: {
        iconClass: 'text-text-informative-default',
        badgeClass:
          'bg-interface-informative-muted-default text-text-informative-muted-default border border-interface-informative-border-default',
        leadingIcon: '/assets/icons/general/info.svg',
      },
      negative: {
        iconClass: 'text-text-danger-default',
        badgeClass:
          'bg-interface-danger-muted-default text-text-danger-default border border-interface-danger-border-default',
        leadingIcon: '/assets/icons/general/system-feedback-warning.svg',
      },
    };

  constructor() {}

  getVariantConfig(): IBadgeVariantConfig {
    if (!this.variant) {
      return null;
    }

    return this._variantConfig[this.variant];
  }
}
